/**
 * Shared pieces for the Meta Ads report pages.
 *
 * Extracted because five pages need the same date-range vocabulary, the same
 * "a metric that could not be computed is a dash, not a zero" formatting, and
 * the same filter bar. Duplicating that across five files is how two reports
 * end up disagreeing about what "last 30 days" means.
 */
import dayjs from "dayjs";
import { AlertTriangle, Info, Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";

export const DATE_PRESETS = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_week", label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "last_90", label: "Last 90 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom", label: "Custom Range" },
];

/** Backend expects DD-MM-YYYY, matching every other report in the CRM. */
export const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = (d) => d.format("DD-MM-YYYY");
    switch (preset) {
        case "today": return { from_date: fmt(today), to_date: fmt(today) };
        case "yesterday": return { from_date: fmt(today.subtract(1, "day")), to_date: fmt(today.subtract(1, "day")) };
        case "last_week": return { from_date: fmt(today.subtract(6, "day")), to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(29, "day")), to_date: fmt(today) };
        case "last_90": return { from_date: fmt(today.subtract(89, "day")), to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom": return (customFrom && customTo)
            ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"), to_date: dayjs(customTo).format("DD-MM-YYYY") }
            : {};
        default: return {};
    }
};

// A null metric means "could not be computed" — no clicks means no CPC. Showing
// 0 would read as "free", so null always renders as a dash.
export const num = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
export const money = (v) => (v == null ? "—"
    : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
export const money2 = (v) => (v == null ? "—"
    : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
export const pct = (v, digits = 2) => (v == null ? "—" : `${Number(v).toFixed(digits)}%`);

export const StatTile = ({ label, value, sub, tone = "default", hint }) => (
    <div
        title={hint}
        className={cn(
            "rounded-xl border p-4 bg-card",
            tone === "spend" && "border-blue-200 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10",
            tone === "leads" && "border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10",
            tone === "cost" && "border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-900/10",
            tone === "bad" && "border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10",
            tone === "default" && "border-border",
        )}
    >
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
);

/** Date + account filter bar shared by every ad report. */
export const AdFilterBar = ({
    datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
    accountId, setAccountId, accounts = [], search, setSearch, onSearch,
    searchPlaceholder = "Search…", children,
}) => (
    <div className="flex flex-wrap items-center gap-2">
        <select
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
        >
            {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>

        {datePreset === "custom" && (
            <>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                    className="h-9 w-auto text-sm" />
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                    className="h-9 w-auto text-sm" />
            </>
        )}

        {setAccountId && (
            <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            >
                <option value="">All ad accounts</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
        )}

        {children}

        {setSearch && (
            <div className="relative flex-1 min-w-[170px] max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onSearch && onSearch()}
                    className="h-9 pl-8 text-sm"
                />
            </div>
        )}
    </div>
);

// ── delivery state ───────────────────────────────────────────────────────────
// Meta's effective_status has a dozen values, but the only distinction a reader
// needs at a glance is "is this spending money right now". Everything else is
// detail for the tooltip.

export const isDelivering = (status) => (status || "").trim().toUpperCase() === "ACTIVE";

const STATUS_LABEL = {
    ACTIVE: "Active",
    PAUSED: "Paused",
    CAMPAIGN_PAUSED: "Campaign paused",
    ADSET_PAUSED: "Ad set paused",
    ARCHIVED: "Archived",
    DELETED: "Deleted",
    IN_PROCESS: "In process",
    PENDING_REVIEW: "In review",
    PENDING_BILLING_INFO: "Billing needed",
    PREAPPROVED: "Pre-approved",
    WITH_ISSUES: "Has issues",
    DISAPPROVED: "Rejected",
};

export const statusLabel = (s) =>
    STATUS_LABEL[(s || "").trim().toUpperCase()] || (s ? s.replace(/_/g, " ").toLowerCase() : "Unknown");

/**
 * Delivery pill. Active gets a filled dot and colour; everything stopped gets a
 * hollow, muted treatment — so a scan of the column reads as "running / not
 * running" without anyone parsing Meta's vocabulary.
 */
export const StatusPill = ({ status, compact = false }) => {
    const live = isDelivering(status);
    const raw = (status || "").trim().toUpperCase();
    const broken = raw === "DISAPPROVED" || raw === "WITH_ISSUES" || raw === "PENDING_BILLING_INFO";
    return (
        <span
            title={`Meta status: ${status || "unknown"}`}
            className={cn(
                "inline-flex items-center gap-1 rounded-full border shrink-0 whitespace-nowrap",
                compact ? "px-1.5 py-0 text-[9px]" : "px-2 py-0.5 text-[10px]",
                live
                    ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 font-medium"
                    : broken
                        ? "border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                        : "border-border bg-muted/60 text-muted-foreground",
            )}
        >
            <span className={cn("h-1.5 w-1.5 rounded-full shrink-0",
                live ? "bg-emerald-500"
                    : broken ? "bg-red-500"
                        : "border border-current bg-transparent")} />
            {statusLabel(status)}
        </span>
    );
};

/**
 * Delivery-state filter. Two states rather than Meta's twelve, because
 * "Paused only" meaning strictly PAUSED would hide everything labelled
 * CAMPAIGN_PAUSED, ADSET_PAUSED or ARCHIVED — most of a real account's
 * stopped inventory.
 */
export const StatusFilterSelect = ({ value, onChange }) => (
    <select
        // Normalised on the way in so a value persisted under the older
        // ACTIVE / PAUSED spelling still selects the matching option instead of
        // rendering the select blank.
        value={{ active: "active", paused: "inactive", inactive: "inactive" }[
            (value || "").toLowerCase()] || ""}
        onChange={(e) => onChange(e.target.value)}
        title="Filter by whether the ad is still delivering"
        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
    >
        <option value="">Active &amp; paused</option>
        <option value="active">Active only</option>
        <option value="inactive">Paused only</option>
    </select>
);

/** Row styling for a stopped entity: present, legible, clearly secondary. */
export const stoppedRowClass = (status) =>
    isDelivering(status) ? "" : "opacity-60 bg-muted/20";

/** Meta grades ads relative to competitors; BELOW_AVERAGE carries a percentile
 *  suffix (BELOW_AVERAGE_35), so match on prefix rather than exact value. */
export const rankingTone = (v) => {
    if (!v || v === "UNKNOWN") return "text-muted-foreground";
    if (v.startsWith("ABOVE")) return "text-emerald-600 dark:text-emerald-400";
    if (v.startsWith("BELOW")) return "text-red-600 dark:text-red-400";
    return "text-amber-600 dark:text-amber-400";
};

export const rankingLabel = (v) => {
    if (!v) return "—";
    if (v === "UNKNOWN") return "Not enough data";
    if (v.startsWith("ABOVE")) return "Above average";
    if (v === "AVERAGE") return "Average";
    if (v.startsWith("BELOW")) {
        const pctile = v.split("_").pop();
        return Number.isNaN(Number(pctile)) ? "Below average" : `Bottom ${pctile}%`;
    }
    return v;
};

/**
 * Banner for the five reports that need each lead to carry its ad id.
 *
 * Deliberately loud when nothing is attributed: an empty table and a table of
 * genuine zeros look identical, and only one of them means "your ads produced
 * nothing". This says which, and how to fix it.
 */
export const AttributionBanner = ({ attribution }) => {
    if (!attribution) return null;
    const { is_wired, total_leads, attributed_leads, coverage_pct, meta_sourced_leads } = attribution;

    if (!is_wired) {
        return (
            <div className="rounded-xl border border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-4">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="space-y-2 min-w-0">
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                            This report is empty because no lead carries an ad id yet
                        </p>
                        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                            {total_leads > 0
                                ? <>Of <strong>{total_leads.toLocaleString("en-IN")}</strong> leads in this
                                    period{meta_sourced_leads > 0 && <> ({meta_sourced_leads.toLocaleString("en-IN")} from
                                        a Meta source)</>}, <strong>none</strong> record which ad produced them.
                                    The report is working — it simply has nothing to group by.</>
                                : <>No leads were created in this period.</>}
                        </p>
                        <div className="text-xs text-amber-800 dark:text-amber-300">
                            <p className="font-medium mb-1">To switch this on:</p>
                            <ol className="list-decimal ml-4 space-y-0.5">
                                <li>Open your Facebook Lead Ads Zap</li>
                                <li>Map <code className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1 rounded">campaign_id</code>,{" "}
                                    <code className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1 rounded">adset_id</code> and{" "}
                                    <code className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1 rounded">ad_id</code> into the lead webhook</li>
                                <li>Attribution starts with the next lead — it cannot be applied to past ones</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Partial coverage still deserves a note: the numbers below are real but
    // cover only the attributed slice, which is easy to mistake for the whole.
    if (coverage_pct != null && coverage_pct < 90) {
        return (
            <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300">
                <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                <span>
                    Covering <strong>{attributed_leads.toLocaleString("en-IN")}</strong> of{" "}
                    <strong>{total_leads.toLocaleString("en-IN")}</strong> leads ({coverage_pct.toFixed(0)}%)
                    — the rest arrived without an ad id and are not counted below.
                </span>
            </div>
        );
    }
    return null;
};

export const EmptyState = ({ title, hint }) => (
    <div className="rounded-xl border border-border bg-card p-10 text-center">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {hint && <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">{hint}</p>}
    </div>
);
